import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { Button, Select } from 'lego-on-react';
import { escapeRegExp, some, uniq } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { OutdoorMapPlatform, OutdoorMapRegionPlatform } from '../OutdoorMap/OutdoorMap';
import { OutdoorSearchInput } from '../OutdoorSearchInput/OutdoorSearchInput';
import { listItem, OutdoorVirtualList } from '../OutdoorVirtualList/OutdoorVirtualList';

import './OutdoorPlatformList.css';
import * as keyset from './OutdoorPlatformList.i18n';

const i18n = i18nFactory(keyset);

export interface OutdoorListItem extends OutdoorMapRegionPlatform, OutdoorMapPlatform {
    type: string;
}

export interface OutdoorPlatformListProps {
    platformsByRegion: OutdoorMapRegionPlatform[];
    onAddressClick?: (platform: listItem) => void;
    onFilter?: (list: (OutdoorMapRegionPlatform | OutdoorMapPlatform)[]) => void;
    onPlatformCheckboxChange: (checked: boolean, platform: OutdoorListItem) => void;
    checkedItems: string[];
    scrollToId?: string;
    highlightId?: string;
    onRegionListToggle: () => void;
    onRegionCheckBoxChange: (data: {
        regionId: string;
        checked: boolean;
        itemIds: string[];
        items: OutdoorMapPlatform[];
    }) => void;
    selectedRegion: string;
}

export interface OutdoorPlatformListState {
    isChecked: boolean;
    searchValue: string;
    checkedItems: string[];
    virtualList: (OutdoorMapRegionPlatform | OutdoorMapPlatform)[];
    openedRegions: {
        [id: string]: boolean;
    };
    operatorsFilter: string;
    formatsFilter: string;
    selectedFilter: boolean;
    scrollToRegionId?: string;
}

export class OutdoorPlatformList extends React.Component<OutdoorPlatformListProps, OutdoorPlatformListState> {
    private block = 'outdoor-platform-list';
    private ref = React.createRef<HTMLDivElement>();

    constructor(props: OutdoorPlatformListProps) {
        super(props);

        this.state = {
            isChecked: false,
            searchValue: '',
            checkedItems: this.props.checkedItems || [],
            virtualList: this.formatToVirtualList(),
            openedRegions: this.props.platformsByRegion.reduce((res, region) => {
                res[region.regionId] = region.regionId === this.props.selectedRegion;

                return res;
            }, {}),
            operatorsFilter: '',
            formatsFilter: '',
            selectedFilter: false,
            scrollToRegionId: ''
        };
    }

    componentDidMount() {
        this.forceUpdate();
    }

    render() {
        return (
            <div className={Bem.cls(this.block)} ref={this.ref}>
                <OutdoorSearchInput
                    placeholder={i18n('find-platforms')}
                    cls={Bem.cls(this.block, 'search')}
                    onChange={this.onSearchValueChange}
                />
                {this.buildFilters()}
                <div className={Bem.cls(this.block, 'dropdown-container')}>{this.buildVirtualList()}</div>
            </div>
        );
    }

    componentWillReceiveProps(nextProps: Readonly<OutdoorPlatformListProps>): void {
        if (nextProps.selectedRegion !== this.props.selectedRegion) {
            const { openedRegions } = this.state;

            for (const regionId in openedRegions) {
                if (nextProps.selectedRegion === regionId) {
                    openedRegions[regionId] = true;
                } else {
                    openedRegions[regionId] = false;
                }
            }

            this.props.onRegionListToggle();

            this.state.virtualList.map((item: OutdoorMapPlatform, i) => {
                if (item.type === 'region' && item.regionId === nextProps.selectedRegion) {
                    this.setState(
                        {
                            openedRegions: openedRegions,
                            scrollToRegionId: nextProps.selectedRegion
                        },
                        () => {
                            this.setState({
                                scrollToRegionId: undefined
                            });
                        }
                    );
                }
            });
        }
    }

    private buildVirtualList() {
        const {
            searchValue,
            openedRegions,
            checkedItems,
            virtualList,
            operatorsFilter,
            formatsFilter,
            selectedFilter,
            scrollToRegionId
        } = this.state;
        const regExpMatch: RegExp = new RegExp(escapeRegExp(searchValue), 'ig');
        const { scrollToId, onAddressClick, onFilter, highlightId } = this.props;
        const regExpTest: RegExp = new RegExp(escapeRegExp(searchValue), 'i');
        const regExpFormats: RegExp = new RegExp(operatorsFilter[0] === i18n('operator') ? '' : operatorsFilter, 'i');
        const regExpOperators: RegExp = new RegExp(formatsFilter[0] === i18n('format') ? '' : formatsFilter, 'i');
        const selectedList = virtualList.filter(
            (item: OutdoorMapPlatform) => checkedItems.indexOf(item.id) !== -1 || item.type === 'region'
        );
        const list = this.filter(
            this.filter(this.filter(selectedFilter ? selectedList : virtualList, regExpOperators), regExpFormats),
            regExpTest
        );

        if (onFilter) {
            onFilter(list);
        }

        return (
            <OutdoorVirtualList
                list={list}
                openedRegions={openedRegions}
                checkedItems={checkedItems}
                scrollToId={scrollToId ? scrollToId : scrollToRegionId}
                highlightId={highlightId}
                onRegionArrowClick={this.onRegionArrowClick}
                onItemChecked={this.onItemChecked}
                onAddressClick={onAddressClick}
                onRegionCheckBoxChange={this.onRegionCheckBoxChange}
                search={{
                    value: this.state.searchValue,
                    test: regExpTest,
                    match: regExpMatch
                }}
                virtualList={virtualList}
            />
        );
    }

    private filter = (items: (OutdoorMapRegionPlatform | OutdoorMapPlatform)[], regExp: RegExp) => {
        return items.filter((item: OutdoorListItem) => {
            if (item.type === 'region') {
                return true;
            } else {
                return (
                    regExp.test(item.caption) ||
                    regExp.test(item.address) ||
                    regExp.test(item.provider) ||
                    (item.gid && regExp.test(item.gid))
                );
            }
        });
    };

    private formatToVirtualList = () => {
        return this.props.platformsByRegion.reduce(
            (result: (OutdoorMapRegionPlatform | OutdoorMapPlatform)[], region: OutdoorMapRegionPlatform) => {
                result.push({
                    type: 'region',
                    regionId: region.regionId,
                    regionText: region.regionText,
                    regionCenter: region.regionCenter,
                    platforms: []
                });

                region.platforms.forEach((platform: OutdoorMapPlatform) => {
                    result.push({
                        ...platform,
                        type: 'platform'
                    });
                });

                return result;
            },
            []
        );
    };

    private onItemChecked = (platform: OutdoorListItem) => {
        const { onPlatformCheckboxChange } = this.props;
        const { checkedItems } = this.state;
        const checked = !checkedItems.includes(platform.id);

        this.setState({
            checkedItems: checked
                ? [...checkedItems, platform.id]
                : checkedItems.filter((checkedId: string) => checkedId !== platform.id)
        });

        onPlatformCheckboxChange(checked, platform);
    };

    private onRegionCheckBoxChange = (data: {
        regionId: string;
        checked: boolean;
        itemIds: string[];
        items: OutdoorMapPlatform[];
    }) => {
        const { checkedItems } = this.state;

        if (data.checked) {
            this.setState({
                checkedItems: uniq([...checkedItems, ...data.itemIds])
            });
        } else {
            this.setState({
                checkedItems: checkedItems.filter((checkedId: string) => !data.itemIds.includes(checkedId))
            });
        }

        this.props.onRegionCheckBoxChange(data);
    };

    private onRegionArrowClick = (data: { regionId: string; isOpen: boolean }) => {
        const { openedRegions } = this.state;
        const newOpenedRegions = { ...openedRegions };

        newOpenedRegions[data.regionId] = data.isOpen;

        this.props.onRegionListToggle();

        this.setState({
            openedRegions: newOpenedRegions
        });
    };

    private getFormats() {
        const { platformsByRegion } = this.props;
        const formats: {}[] = [
            {
                text: i18n('all'),
                val: i18n('format')
            }
        ];

        platformsByRegion.map((region: OutdoorMapRegionPlatform) => {
            region.platforms.map(item => {
                const itemText = item.format.charAt(0).toUpperCase() + item.format.slice(1);

                if (!some(formats, ['text', itemText])) {
                    formats.push({
                        text: itemText,
                        val: itemText
                    });
                }
            });
        });

        return formats;
    }

    private getOperators() {
        const { platformsByRegion } = this.props;
        const operators: {}[] = [
            {
                text: i18n('all'),
                val: i18n('operator')
            }
        ];

        platformsByRegion.map((region: OutdoorMapRegionPlatform) => {
            region.platforms.map(item => {
                if (!some(operators, ['text', item.provider])) {
                    operators.push({
                        text: item.provider,
                        val: item.provider
                    });
                }
            });
        });

        return operators;
    }

    private buildFilters() {
        const { operatorsFilter, formatsFilter } = this.state;
        const operator = operatorsFilter ? operatorsFilter : i18n('operator');
        const format = formatsFilter ? formatsFilter : i18n('format');

        return (
            <div className={Bem.cls(this.block, 'filters-container')}>
                <div className={Bem.cls(this.block, 'main-filters')}>
                    <Select
                        cls={classNames(
                            Bem.cls(this.block, 'filter', {
                                type: 'operators'
                            }),
                            Bem.cls('select2', '', { outdoor: 'yes' })
                        )}
                        theme="normal"
                        size="s"
                        type="radio"
                        items={this.getOperators()}
                        placeholder={operator}
                        popup={{
                            scope: this.ref.current || undefined
                        }}
                        text={operator}
                        val={operator}
                        onChange={this.onOperatorsFilterChange}
                    />
                    <Select
                        cls={classNames(
                            Bem.cls(this.block, 'filter', { type: 'formats' }),
                            Bem.cls('select2', '', { outdoor: 'yes' })
                        )}
                        theme="normal"
                        size="s"
                        type="radio"
                        placeholder={format}
                        popup={{
                            scope: this.ref.current || undefined
                        }}
                        val={format}
                        text={format}
                        items={this.getFormats()}
                        onChange={this.onFormatsFilterChange}
                    />
                    <Button
                        cls={Bem.cls(this.block, 'filter')}
                        theme="normal"
                        type="check"
                        size="s"
                        view="classic"
                        pin="circle-circle"
                        text={i18n('selected')}
                        onClick={this.onSelectedFilterChange}
                    />
                </div>
                {/*<Dropdown theme="normal" size="s" switcher="button2">*/}
                {/*<Dropdown.Switcher>...</Dropdown.Switcher>*/}
                {/*</Dropdown>*/}
            </div>
        );
    }

    private onOperatorsFilterChange = (value: string) => {
        this.setState({ operatorsFilter: value });
    };

    private onFormatsFilterChange = (value: string) => {
        this.setState({ formatsFilter: value });
    };

    private onSearchValueChange = (value: string): void => {
        this.setState({ searchValue: value });
    };

    private onSelectedFilterChange = () => {
        const { selectedFilter } = this.state;

        this.setState({ selectedFilter: !selectedFilter });
    };
}
